import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import { SearchUtils } from '@spotify/confidence-core-react';
import {
  ChatRefFragment,
  ConversationsV2ChatSource,
  ConversationsV2ChatState,
  getTypeOrNull,
  useChatUpdatedSubscription,
  useChatsLazyQuery,
} from '@spotify/confidence-graphql';

import { type ChatStatus, getChatStatus } from '../../utils/getChatStatus';
import { useChats } from '../useChats';

export type FinishedChatStatus = 'completed' | 'attention' | 'error';

export type ChatActivityStatus = 'running' | FinishedChatStatus;

export type FinishedChat = {
  chatName: string;
  displayName: string;
  projectName: string | undefined;
  status: FinishedChatStatus;
};

type ChatFinishedListener = (chat: FinishedChat) => void;

type RunningChatsSnapshot = {
  statuses: ReadonlyMap<string, ChatActivityStatus>;
  running: ReadonlySet<string>;
  tracked: ReadonlySet<string>;
};

type RunningChatsStore = {
  watch: (chatName: string) => void;
  unwatch: (chatName: string) => void;
  track: (chatName: string) => () => void;
  finish: (chat: FinishedChat) => void;
  observeRunning: (
    chatName: string,
    pendingInputKey: string | undefined,
    errorKey: string | undefined,
    observedAt: number,
  ) => number | undefined;
  settleableAt: (chatName: string) => number | undefined;
  pendingInputKeyWhenRunning: (chatName: string) => string | undefined;
  errorKeyWhenRunning: (chatName: string) => string | undefined;
  subscribe: (callback: () => void) => () => void;
  subscribeToFinished: (listener: ChatFinishedListener) => () => void;
  getSnapshot: () => RunningChatsSnapshot;
};

const FINISHED_STATUSES: ReadonlySet<string> = new Set<FinishedChatStatus>([
  'completed',
  'attention',
  'error',
]);

const isFinishedStatus = (status: string): status is FinishedChatStatus =>
  FINISHED_STATUSES.has(status);

const buildSnapshot = (
  statuses: ReadonlyMap<string, ChatActivityStatus>,
  trackedCounts: ReadonlyMap<string, number>,
): RunningChatsSnapshot => ({
  statuses: new Map(statuses),
  running: new Set(
    Array.from(statuses)
      .filter(([, status]) => status === 'running')
      .map(([chatName]) => chatName),
  ),
  tracked: new Set(trackedCounts.keys()),
});

const RUNNING_CHATS_POLL_INTERVAL_MS = 30_000;
const SETTLE_WITHOUT_RUNNING_AFTER_MS = 10_000;

type ChatWatch = {
  watchedAt: number;
  runningObservedAt: number | undefined;
  pendingInputKeyWhenRunning: string | undefined;
  errorKeyWhenRunning: string | undefined;
};

const CHAT_NAME_PREFIX = 'chats/';

type BackendActivityStatus = Exclude<ChatActivityStatus, 'completed'>;

const BACKEND_ACTIVITY_STATUSES: ReadonlySet<string> =
  new Set<BackendActivityStatus>(['running', 'attention', 'error']);

const isBackendActivityStatus = (
  status: ChatStatus,
): status is BackendActivityStatus => BACKEND_ACTIVITY_STATUSES.has(status);

const EMPTY_SNAPSHOT: RunningChatsSnapshot = buildSnapshot(
  new Map(),
  new Map(),
);

const EMPTY_BACKEND_STATUSES: ReadonlyMap<string, ChatStatus> = new Map();

const RunningChatsContext = createContext<RunningChatsStore | null>(null);

const TrackedChatStatusesContext = createContext<
  ReadonlyMap<string, ChatStatus>
>(EMPTY_BACKEND_STATUSES);

const buildRunningChatsFilter = (
  chatNames: Iterable<string>,
): string | undefined => {
  const chatNamesFilter = Array.from(chatNames)
    .map(name => `chat.name:${SearchUtils.escapeQuery(name)}`)
    .join(' OR ');

  if (!chatNamesFilter) return undefined;

  return `(${chatNamesFilter}) AND chat.source:${ConversationsV2ChatSource.Web} AND NOT chat.system_created:true`;
};

const buildRunningChatsListFilter = (
  chatNames: Iterable<string>,
): string | undefined => {
  const quotedNames = Array.from(chatNames).map(name => `"${name}"`);
  if (!quotedNames.length) return undefined;
  return `name:(${quotedNames.join(' OR ')})`;
};

type ChatWithStatus = Pick<
  ChatRefFragment,
  'name' | 'displayName' | 'state' | 'assistantOutcome' | 'project'
>;

const getPendingInputKey = (
  assistantOutcome: ChatWithStatus['assistantOutcome'],
): string | undefined => {
  const { toolConfirmation, askQuestion } = assistantOutcome ?? {};
  if (!toolConfirmation && !askQuestion) return undefined;
  return JSON.stringify({
    tool: toolConfirmation?.tool,
    input: toolConfirmation?.input,
    question: askQuestion?.question,
  });
};

const finishChat = (
  store: RunningChatsStore,
  chat: ChatWithStatus,
  status: FinishedChatStatus,
) =>
  store.finish({
    chatName: chat.name,
    displayName: chat.displayName ?? chat.name,
    projectName: getTypeOrNull(chat.project)?.name,
    status,
  });

const applyChatUpdate = (
  store: RunningChatsStore,
  chat: ChatWithStatus,
  observedAt: number,
): number | undefined => {
  if (chat.state === ConversationsV2ChatState.Running) {
    const pendingInputKey = getPendingInputKey(chat.assistantOutcome);
    const errorKey = chat.assistantOutcome?.error
      ? JSON.stringify(chat.assistantOutcome.error)
      : undefined;
    const settleableAt = store.observeRunning(
      chat.name,
      pendingInputKey,
      errorKey,
      observedAt,
    );
    if (settleableAt !== undefined) return settleableAt;
    if (
      errorKey !== undefined &&
      errorKey !== store.errorKeyWhenRunning(chat.name)
    ) {
      finishChat(store, chat, 'error');
    } else if (
      pendingInputKey !== undefined &&
      pendingInputKey !== store.pendingInputKeyWhenRunning(chat.name)
    ) {
      finishChat(store, chat, 'attention');
    }
    return undefined;
  }
  const settleableAt = store.settleableAt(chat.name);
  if (settleableAt === undefined) return undefined;
  if (observedAt < settleableAt) return settleableAt;

  const status = getChatStatus(chat.state, chat.assistantOutcome);
  if (!isFinishedStatus(status)) return undefined;

  finishChat(store, chat, status);
  return undefined;
};

const getTrackedChatStatus = (
  { state, assistantOutcome }: ChatWithStatus,
  sessionStatus: ChatActivityStatus | undefined,
): ChatStatus =>
  getChatStatus(
    state,
    state === ConversationsV2ChatState.Running &&
      assistantOutcome &&
      (sessionStatus !== 'error' || getPendingInputKey(assistantOutcome))
      ? { ...assistantOutcome, error: null }
      : assistantOutcome,
  );

const resolveChatActivityStatus = (
  sessionStatus: ChatActivityStatus | undefined,
  backendStatus: ChatStatus | undefined,
): ChatActivityStatus | undefined => {
  if (sessionStatus === 'running') return sessionStatus;
  if (backendStatus === undefined) return sessionStatus;
  if (isBackendActivityStatus(backendStatus)) return backendStatus;
  return sessionStatus === undefined ? undefined : 'completed';
};

const TrackedChatStatuses = ({
  store,
  children,
}: {
  store: RunningChatsStore;
  children: ReactNode;
}) => {
  const { tracked, statuses: sessionStatuses } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
  );
  const filter = useMemo(() => buildRunningChatsListFilter(tracked), [tracked]);
  const { chats } = useChats({
    filter,
    skip: !filter,
    fetchPolicy: 'cache-and-network',
  });
  const statuses = useMemo(
    () =>
      new Map(
        (chats ?? []).map(chat => [
          chat.name,
          getTrackedChatStatus(chat, sessionStatuses.get(chat.name)),
        ]),
      ),
    [chats, sessionStatuses],
  );

  return (
    <TrackedChatStatusesContext.Provider value={statuses}>
      {children}
    </TrackedChatStatusesContext.Provider>
  );
};

const RunningChatsUpdates = ({ store }: { store: RunningChatsStore }) => {
  const { running, tracked } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
  );
  const filter = useMemo(
    () => buildRunningChatsFilter(new Set([...running, ...tracked])),
    [running, tracked],
  );
  const listFilter = useMemo(
    () => buildRunningChatsListFilter(running),
    [running],
  );
  const [fetchRunningChats] = useChatsLazyQuery({
    fetchPolicy: 'network-only',
  });

  useChatUpdatedSubscription({
    variables: filter ? { filter } : undefined,
    skip: !filter,
    onData({ data: { data } }) {
      const chat = data?.chatUpdated?.chat;
      if (chat) applyChatUpdate(store, chat, Date.now());
    },
  });

  useEffect(() => {
    if (!listFilter) return undefined;
    let active = true;
    let nextCheck: ReturnType<typeof setTimeout> | undefined;

    const checkRunningChats = async () => {
      const startedAt = Date.now();
      const result = await fetchRunningChats({
        variables: { filter: listFilter },
      }).catch(() => undefined);
      if (!active) return;

      const response = result?.data?.chats;
      const chats =
        response?.__typename === 'ConversationsV2ListChatsResponse'
          ? response.chats
          : [];
      const nextCheckAt = chats.reduce(
        (earliest, chat) =>
          Math.min(
            earliest,
            applyChatUpdate(store, chat, startedAt) ?? earliest,
          ),
        startedAt + RUNNING_CHATS_POLL_INTERVAL_MS,
      );
      nextCheck = setTimeout(
        checkRunningChats,
        Math.max(0, nextCheckAt - Date.now()),
      );
    };

    checkRunningChats();
    return () => {
      active = false;
      clearTimeout(nextCheck);
    };
  }, [listFilter, fetchRunningChats, store]);

  return null;
};

export const RunningChatsProvider = ({ children }: { children: ReactNode }) => {
  const statusesRef = useRef(new Map<string, ChatActivityStatus>());
  const watchesRef = useRef(new Map<string, ChatWatch>());
  const snapshotRef = useRef<RunningChatsSnapshot>(EMPTY_SNAPSHOT);
  const trackedCountsRef = useRef(new Map<string, number>());
  const listenersRef = useRef(new Set<() => void>());
  const finishedListenersRef = useRef(new Set<ChatFinishedListener>());

  const emit = useCallback(() => {
    snapshotRef.current = buildSnapshot(
      statusesRef.current,
      trackedCountsRef.current,
    );
    for (const listener of listenersRef.current) {
      listener();
    }
  }, []);

  const watch = useCallback(
    (chatName: string) => {
      if (statusesRef.current.get(chatName) !== 'running') {
        statusesRef.current.set(chatName, 'running');
        watchesRef.current.set(chatName, {
          watchedAt: Date.now(),
          runningObservedAt: undefined,
          pendingInputKeyWhenRunning: undefined,
          errorKeyWhenRunning: undefined,
        });
        emit();
      }
    },
    [emit],
  );

  const unwatch = useCallback(
    (chatName: string) => {
      watchesRef.current.delete(chatName);
      if (statusesRef.current.delete(chatName)) {
        emit();
      }
    },
    [emit],
  );

  const track = useCallback(
    (chatName: string) => {
      const trackedCounts = trackedCountsRef.current;
      trackedCounts.set(chatName, (trackedCounts.get(chatName) ?? 0) + 1);
      if (trackedCounts.get(chatName) === 1) emit();
      return () => {
        const count = trackedCounts.get(chatName) ?? 0;
        if (count > 1) {
          trackedCounts.set(chatName, count - 1);
        } else if (trackedCounts.delete(chatName)) {
          emit();
        }
      };
    },
    [emit],
  );

  const finish = useCallback(
    (chat: FinishedChat) => {
      if (statusesRef.current.get(chat.chatName) !== 'running') return;
      watchesRef.current.delete(chat.chatName);
      statusesRef.current.set(chat.chatName, chat.status);
      emit();
      for (const listener of finishedListenersRef.current) {
        listener(chat);
      }
    },
    [emit],
  );

  const observeRunning = useCallback(
    (
      chatName: string,
      pendingInputKey: string | undefined,
      errorKey: string | undefined,
      observedAt: number,
    ) => {
      const chatWatch = watchesRef.current.get(chatName);
      if (chatWatch && chatWatch.runningObservedAt === undefined) {
        chatWatch.runningObservedAt = Date.now();
        const isEarlyInTurn =
          observedAt < chatWatch.watchedAt + SETTLE_WITHOUT_RUNNING_AFTER_MS;
        chatWatch.pendingInputKeyWhenRunning = isEarlyInTurn
          ? pendingInputKey
          : undefined;
        chatWatch.errorKeyWhenRunning = isEarlyInTurn ? errorKey : undefined;
      }
      if (chatWatch && chatWatch.runningObservedAt !== undefined) {
        if (observedAt < chatWatch.runningObservedAt) {
          return chatWatch.runningObservedAt;
        }
        if (errorKey === undefined) {
          chatWatch.errorKeyWhenRunning = undefined;
        }
      }
      return undefined;
    },
    [],
  );

  const pendingInputKeyWhenRunning = useCallback(
    (chatName: string) =>
      watchesRef.current.get(chatName)?.pendingInputKeyWhenRunning,
    [],
  );

  const errorKeyWhenRunning = useCallback(
    (chatName: string) => watchesRef.current.get(chatName)?.errorKeyWhenRunning,
    [],
  );

  const settleableAt = useCallback((chatName: string) => {
    const chatWatch = watchesRef.current.get(chatName);
    if (!chatWatch) return undefined;
    return (
      chatWatch.runningObservedAt ??
      chatWatch.watchedAt + SETTLE_WITHOUT_RUNNING_AFTER_MS
    );
  }, []);

  const subscribe = useCallback((callback: () => void) => {
    listenersRef.current.add(callback);
    return () => listenersRef.current.delete(callback);
  }, []);

  const subscribeToFinished = useCallback((listener: ChatFinishedListener) => {
    finishedListenersRef.current.add(listener);
    return () => finishedListenersRef.current.delete(listener);
  }, []);

  const getSnapshot = useCallback(() => snapshotRef.current, []);

  const store = useMemo<RunningChatsStore>(
    () => ({
      watch,
      unwatch,
      track,
      finish,
      observeRunning,
      settleableAt,
      pendingInputKeyWhenRunning,
      errorKeyWhenRunning,
      subscribe,
      subscribeToFinished,
      getSnapshot,
    }),
    [
      watch,
      unwatch,
      track,
      finish,
      observeRunning,
      settleableAt,
      pendingInputKeyWhenRunning,
      errorKeyWhenRunning,
      subscribe,
      subscribeToFinished,
      getSnapshot,
    ],
  );

  return (
    <RunningChatsContext.Provider value={store}>
      <RunningChatsUpdates store={store} />
      <TrackedChatStatuses store={store}>{children}</TrackedChatStatuses>
    </RunningChatsContext.Provider>
  );
};

export const useWatchRunningChat = () => {
  const store = useContext(RunningChatsContext);
  const noop = useCallback(() => {}, []);
  return {
    watch: store?.watch ?? noop,
    unwatch: store?.unwatch ?? noop,
  };
};

const EMPTY_SUBSCRIBE = () => () => {};
const GET_EMPTY_SNAPSHOT = () => EMPTY_SNAPSHOT;

const useRunningChatsSnapshot = (): RunningChatsSnapshot => {
  const store = useContext(RunningChatsContext);
  return useSyncExternalStore(
    store?.subscribe ?? EMPTY_SUBSCRIBE,
    store?.getSnapshot ?? GET_EMPTY_SNAPSHOT,
  );
};

export const useRunningChatNames = (): ReadonlySet<string> =>
  useRunningChatsSnapshot().running;

export const useChatActivityStatuses = (): ReadonlyMap<
  string,
  ChatActivityStatus
> => useRunningChatsSnapshot().statuses;

export const useChatActivityStatus = (
  chatName: string,
): ChatActivityStatus | undefined => {
  const store = useContext(RunningChatsContext);
  const isChat = chatName.startsWith(CHAT_NAME_PREFIX);
  useEffect(
    () => (store && isChat ? store.track(chatName) : undefined),
    [store, isChat, chatName],
  );
  const sessionStatus = useChatActivityStatuses().get(chatName);
  const backendStatus = useContext(TrackedChatStatusesContext).get(chatName);
  return resolveChatActivityStatus(sessionStatus, backendStatus);
};

export const useChatFinishedListener = (listener: ChatFinishedListener) => {
  const store = useContext(RunningChatsContext);
  const listenerRef = useRef(listener);

  useEffect(() => {
    listenerRef.current = listener;
  }, [listener]);

  useEffect(
    () => store?.subscribeToFinished(chat => listenerRef.current(chat)),
    [store],
  );
};
